import {
  Alert,
  Button,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CaptureSheet } from "../../capture";
import {
  InlineError,
  MoneyField,
  SectionCard,
} from "../../components/ui";
import { api, type BillForCollection } from "../../lib/api";
import { METHODS, type Method } from "./methods";
import { formatINR } from "../../lib/money";
import { Page } from "../../shell/Page";

// METHODS and Method now live in ./methods, shared with RecordPayment.
// See that file for why they are not copied.

/**
 * REQ-MKT-3/4/5: record a collection against an existing bill, with proof.
 *
 * Two steps: find the bill by the reference on the customer's copy, then
 * capture. Part payments are expected (Q10) and the amount is capped at the
 * outstanding balance — enforced here for a quick answer and again inside a
 * serializable transaction on the server, which is what actually prevents two
 * agents over-collecting on the same bill at once.
 */
export function PaymentCollect() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Arriving from the Outstanding list, which links with the bill it means.
  // Without this the link landed on an empty lookup form and the agent had to
  // type in a reference the app had just shown them.
  const [reference, setReference] = useState(params.get("reference") ?? "");
  const [bill, setBill] = useState<BillForCollection>();
  const [amount, setAmount] = useState<number | string>("");
  const [method, setMethod] = useState<Method>("CASH");
  const [instrument, setInstrument] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [receipt, setReceipt] = useState<{
    amountCollected: string;
    outstandingAfter: string;
    fullySettled: boolean;
  }>();

  /**
   * Cash goes in the cash book; every other method goes in the bank book,
   * and the server REFUSES a bank receipt with no instrument reference -
   * without one it cannot be reconciled against the statement. So the field
   * is required exactly when the method makes it required, and the submit is
   * gated on it rather than letting the agent capture a photo, take a
   * signature and only then be told no.
   */
  const instrumentLabel = METHODS.find((m) => m.value === method)?.instrument ?? null;
  const instrumentNeeded = instrumentLabel !== null;
  const instrumentValid = !instrumentNeeded || instrument.trim().length > 0;

  const outstanding = bill ? Number(bill.outstanding) : 0;
  const amountValue = Number(amount);
  const amountValid =
    String(amount).trim() !== "" &&
    Number.isFinite(amountValue) &&
    amountValue > 0 &&
    amountValue <= outstanding + 0.005;

  /**
   * The lookup itself, without a form event.
   *
   * Split out so arriving with ?reference= can run it directly. It was only
   * reachable from a submit handler, which is why the link from the
   * Outstanding list did nothing.
   */
  async function lookupReference(value: string) {
    setError(undefined);
    setBusy(true);
    try {
      const result = await api.lookupBill(value.trim());
      setBill(result.bill);
      setAmount(result.bill.outstanding);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not find that bill.",
      );
    } finally {
      setBusy(false);
    }
  }

  const fromUrl = params.get("reference");
  useEffect(() => {
    // Once, on arrival. Not on every render, and not when somebody is typing
    // a different reference into the field.
    if (fromUrl && fromUrl.trim().length >= 3) void lookupReference(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  async function lookup(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const result = await api.lookupBill(reference.trim());
      setBill(result.bill);
      // Pre-fill with the full outstanding balance: settling in full is the
      // common case, and a part payment is then one edit rather than typing
      // the whole figure.
      setAmount(result.bill.outstanding);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not find that bill.");
    } finally {
      setBusy(false);
    }
  }

  if (receipt) {
    return (
      <Page title="Payment recorded">
        <Alert
          variant="light"
          color="teal"
          radius="md"
          icon={<IconCircleCheck size={20} stroke={1.8} />}
          title={`${formatINR(receipt.amountCollected)} collected`}
        >
          {receipt.fullySettled
            ? "Bill settled in full."
            : `${formatINR(receipt.outstandingAfter)} still outstanding.`}
        </Alert>
        <Button
          size="md"
          onClick={() => {
            setReceipt(undefined);
            setBill(undefined);
            setReference("");
            setAmount("");
            setInstrument("");
            setNote("");
          }}
        >
          Collect another
        </Button>
        <Button variant="default" size="md" onClick={() => navigate("/collections")}>
          Back to Collections
        </Button>
      </Page>
    );
  }

  return (
    <Page title="Collect payment" subtitle="Against an existing bill" back="/collections">
      {!bill ? (
        <SectionCard title="Find the bill">
          <form onSubmit={lookup}>
            <Stack gap="sm">
              <TextInput
                id="ref"
                label="Bill or order number"
                value={reference}
                onChange={(event) => setReference(event.currentTarget.value)}
                placeholder="e.g. BILL-2609-0042"
                autoFocus
                required
                description="The reference printed on the customer's copy."
              />
              {error && <InlineError>{error}</InlineError>}
              <Button
                type="submit"
                size="md"
                fullWidth
                loading={busy}
                disabled={reference.trim().length < 3}
              >
                Find bill
              </Button>
            </Stack>
          </form>
        </SectionCard>
      ) : (
        <>
          <SectionCard
            title={bill.reference}
            action={
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  setBill(undefined);
                  setError(undefined);
                }}
              >
                Change
              </Button>
            }
          >
            <Stack gap="sm">
              <Stack gap={2}>
                <Text fz="lg" fw={600} c="gray.9">
                  {bill.customer.name}
                </Text>
                <Text fz="sm" c="gray.6" className="num">
                  {bill.customer.phone}
                </Text>
                <Text fz="sm" c="gray.6">
                  Order {bill.orderReference} ·{" "}
                  {bill.orderType.replace(/_/g, " ").toLowerCase()}
                </Text>
              </Stack>
              <Stack gap={6} component="dl" m={0}>
                <Money label="Bill total" value={bill.totalAmount} />
                <Money label="Already collected" value={bill.collected} />
                <Money label="Outstanding" value={outstanding} strong />
              </Stack>
            </Stack>
          </SectionCard>

          <CaptureSheet
            kind="PAYMENT"
            title="Proof of collection"
            description="Photo and location are required."
            submitLabel={
              amountValid ? `Record ${formatINR(amountValue)}` : "Record payment"
            }
            disabled={!amountValid || !instrumentValid}
            onSubmit={async (capture) => {
              const result = await api.recordPayment({
                billId: bill.id,
                amount: amountValue,
                paymentMethod: method,
                // Under its own name, which is the one the server checks.
                ...(instrument.trim() ? { instrumentNumber: instrument.trim() } : {}),
                ...(note.trim() ? { referenceNote: note.trim() } : {}),
                capture: {
                  ...(capture.gps ? { gps: capture.gps } : {}),
                  ...(capture.photoDataUrl ? { photoDataUrl: capture.photoDataUrl } : {}),
                  ...(capture.notes ? { notes: capture.notes } : {}),
                  clientCapturedAt: capture.clientCapturedAt,
                },
              });
              setReceipt(result);
            }}
          >
            <SectionCard title="What was paid" required>
              <Stack gap="sm">
                <MoneyField
                  id="amount"
                  label="Amount collected"
                  withAsterisk
                  min={0.01}
                  max={outstanding}
                  value={amount}
                  onChange={setAmount}
                  error={
                    String(amount).trim() !== "" && !amountValid
                      ? `Enter an amount between ${formatINR(0.01)} and ${formatINR(
                          outstanding,
                        )}.`
                      : undefined
                  }
                />
                {amountValid && amountValue < outstanding && (
                  <Alert variant="light" color="yellow" radius="md" p="sm">
                    <Text fz="sm">
                      Part payment — {formatINR(outstanding - amountValue)} will
                      remain outstanding.
                    </Text>
                  </Alert>
                )}

                {/* A 2x2 grid of 42px cells, not a dropdown.
                    
                    Four options, chosen at a customer's door, often one-handed
                    - a closed select hides three of them behind a tap and
                    makes the fourth a guess. All four are on screen, and the
                    one in force is obvious without opening anything. */}
                <Stack gap={6}>
                  <Text fz="sm" fw={500} component="label" id="method-label">
                    Payment method
                  </Text>
                  <SimpleGrid
                    cols={2}
                    spacing={8}
                    role="radiogroup"
                    aria-labelledby="method-label"
                  >
                    {METHODS.map((option) => {
                      const chosen = method === option.value;
                      return (
                        <UnstyledButton
                          key={option.value}
                          role="radio"
                          aria-checked={chosen}
                          onClick={() => setMethod(option.value)}
                          h={42}
                          px={12}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "var(--mantine-radius-md)",
                            // 2px so the chosen cell is unmistakable, and the
                            // unchosen ones keep a 2px transparent border so
                            // nothing shifts by a pixel when you pick one.
                            border: chosen
                              ? "2px solid #3b5bdb"
                              : "2px solid var(--mantine-color-gray-3)",
                            background: chosen ? "#edf2ff" : "var(--mantine-color-white)",
                            color: chosen ? "#3b5bdb" : "var(--mantine-color-gray-8)",
                            fontWeight: chosen ? 600 : 400,
                          }}
                        >
                          <Text fz="sm" style={{ color: "inherit", fontWeight: "inherit" }}>
                            {option.label}
                          </Text>
                        </UnstyledButton>
                      );
                    })}
                  </SimpleGrid>
                  {/* Said once, here, rather than only inside the field that
                      appears: it is WHY the field appears. */}
                  <Text fz={12} c="gray.6">
                    Everything except cash needs a reference — it is what
                    reconciles the receipt against the bank statement. Cash
                    goes in the cash book and needs none.
                  </Text>
                </Stack>

                {instrumentNeeded && (
                  <TextInput
                    id="instrument"
                    label={instrumentLabel}
                    withAsterisk
                    value={instrument}
                    onChange={(event) => setInstrument(event.currentTarget.value)}
                    maxLength={60}
                    description={`Recorded in the bank book. ${instrumentLabel} is what reconciles this against the statement.`}
                    error={
                      instrument.trim() === "" && instrument !== ""
                        ? `Enter the ${instrumentLabel.toLowerCase()}.`
                        : undefined
                    }
                  />
                )}

                <TextInput
                  id="note"
                  label="Note"
                  description="Optional. Anything worth recording alongside the payment."
                  value={note}
                  onChange={(event) => setNote(event.currentTarget.value)}
                  maxLength={200}
                />
              </Stack>
            </SectionCard>
          </CaptureSheet>
        </>
      )}
    </Page>
  );
}

function Money({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string | number;
  strong?: boolean;
}) {
  return (
    <Group justify="space-between" gap="md" wrap="nowrap">
      <Text component="dt" fz="sm" c="gray.6">
        {label}
      </Text>
      <Text
        component="dd"
        fz={strong ? "lg" : "sm"}
        fw={strong ? 700 : 500}
        c="gray.9"
        m={0}
        className="num"
      >
        {formatINR(value)}
      </Text>
    </Group>
  );
}
